import type { ReactNode } from "react";
import { Check } from "lucide-react";

import { cx } from "../../lib/cx";

interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  /** Optional trailing content (e.g. a count badge). */
  trailing?: ReactNode;
}

/**
 * Accessible checkbox. The native input is kept (visually hidden) for keyboard
 * and screen-reader support; the accent-filled box is the visible control.
 */
export function Checkbox({ checked, onChange, label, trailing }: CheckboxProps) {
  return (
    <label className="flex cursor-pointer items-center gap-2 py-1 text-sm text-text-primary">
      <span className="relative inline-flex h-4 w-4 shrink-0 items-center justify-center">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="peer absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
        <span
          className={cx(
            "inline-flex h-4 w-4 items-center justify-center rounded border transition-colors",
            "peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent",
            checked
              ? "border-accent bg-accent text-text-onAccent"
              : "border-border-strong bg-surface-overlay text-transparent",
          )}
        >
          <Check size={12} strokeWidth={3} />
        </span>
      </span>
      <span className="flex-1">{label}</span>
      {trailing}
    </label>
  );
}
